import { useState } from 'react';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import type { SubmitTaskRequestTypeEnum } from '@bvengineering/cas-member-gateway-service-client';
import {
  Button,
  Icon,
  IconButton,
  LoadingIcon,
  Modal,
  Typography,
  useToast,
} from '@/primer/components';
import { PageColumn } from '@/components/layout';
import { useCampaign, useSubmitTask } from '@/api';
import { useIsProviderConnected } from '@/api/queries/settings/useQuerySocialConnections';
import { getTaskDisplay, isSupportedTaskType } from '@/utils/taskTypes';
import { isTaskSubmittable, isTaskValidating, isTaskRejected } from '@/utils/taskState';
import { parseTaskRejectionReason } from '@/utils/taskRejectionReasons';

interface NumberedStepProps {
  index: number;
  title: string;
  children?: React.ReactNode;
}

/** Read-only pill displaying a value (account handle or mention) to include in the post. */
function ValuePill({ value }: { value: string }) {
  return (
    <div className="w-full rounded-medium bg-interactive-quiet px-4 py-3">
      <Typography variant="body" size="medium" as="span" decoration={{ bold: true }}>
        {value}
      </Typography>
    </div>
  );
}

/** A single numbered step in the "Follow these steps" list. */
function NumberedStep({ index, title, children }: NumberedStepProps) {
  return (
    <li className="flex gap-3">
      <Typography variant="body" size="medium" as="span" decoration={{ bold: true }}>
        {index}.
      </Typography>
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        <Typography variant="body" size="medium" as="span">
          {title}
        </Typography>
        {children}
      </div>
    </li>
  );
}

/** Shown while the submitted post is being verified by the backend. */
function VerifyingState() {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <Icon name="clock" size="xl" color="interactive" />
      <Typography variant="header" size="h2" as="h2">
        Verifying your post
      </Typography>
      <Typography variant="body" size="small" color="bodylight">
        Usually takes a few minutes. We will update your task once verification is complete.
      </Typography>
    </div>
  );
}

/** Explains why a post may have failed verification. */
function ReasonsForFailureModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const reasons = [
    "Your post isn't public.",
    'Required mentions are missing.',
    "The post hasn't propagated yet.",
  ];
  return (
    <Modal isOpen={isOpen} onClose={onClose} primaryButton={{ label: 'Got it', onClick: onClose }}>
      <div className="flex flex-col gap-4">
        <div className="flex justify-center">
          <Icon name="circle-info-filled" size="large" color="interactive" />
        </div>
        <div className="text-center">
          <Typography variant="header" size="h2" as="h2">
            Possible reasons for failure
          </Typography>
        </div>
        <ul className="flex flex-col gap-2 list-disc pl-5">
          {reasons.map((reason) => (
            <li key={reason}>
              <Typography variant="body" size="medium" as="span">
                {reason}
              </Typography>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

/**
 * Post on Instagram — social task submission screen. Renders the post steps
 * (performable/rejected), an explicit "I've posted" submit, and the in-place
 * verifying state; rejected tasks can be re-submitted via "Verify again".
 */
export function PostOnInstagram() {
  // strict:false because this route is un-nested from the tasks hub (trailing-
  // underscore id). Missing/invalid params fail fast to the Task Not Found state below.
  const { id, taskId } = useParams({ strict: false });
  const navigate = useNavigate();
  const router = useRouter();
  const { showToast } = useToast();

  const campaignId = Number(id);
  const numericTaskId = Number(taskId);

  const { data: campaign, isLoading, error } = useCampaign(campaignId);
  const { isConnected, username: instagramUsername } = useIsProviderConnected('INSTAGRAM');
  const submitTask = useSubmitTask();
  const [showReasons, setShowReasons] = useState(false);

  const task = campaign?.tasks?.find((candidate) => candidate.id === numericTaskId);

  const backToTasks = () => {
    // `id` comes from useParams({ strict: false }); guard against a missing/invalid
    // param so we never navigate to `/campaign/NaN/tasks`.
    if (Number.isFinite(campaignId)) {
      navigate({ to: '/campaign/$id/tasks', params: { id: campaignId } });
    } else {
      navigate({ to: '/dashboard' });
    }
  };

  // TODO: replace stubbed help links with real guides once available.
  const handleHelpStub = () => {
    showToast({ variant: 'info', message: 'This guide is coming soon.' });
  };

  // On success we stay on-screen; the refetched VALIDATING state renders the verifying view.
  const handleSubmit = async () => {
    // Guard on the same conditions as `canSubmit` (below) so the mutation can't fire
    // for unsupported/non-submittable tasks or a disconnected account, even if invoked
    // outside the disabled-button path.
    if (
      !task ||
      submitTask.isPending ||
      !isConnected ||
      !isSupportedTaskType(task.type) ||
      !isTaskSubmittable(task.state)
    ) {
      return;
    }
    try {
      await submitTask.mutateAsync({
        taskId: task.id,
        campaignId,
        type: task.type as SubmitTaskRequestTypeEnum,
      });
    } catch {
      showToast({ variant: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingIcon size="xl" />
      </div>
    );
  }

  if (error || !campaign || !task) {
    return (
      <PageColumn className="text-center py-8">
        <div className="flex flex-col items-center gap-4">
          <Typography variant="header" size="h1" color="error" as="h1">
            Task Not Found
          </Typography>
          <Button onClick={backToTasks}>Back to Tasks</Button>
        </div>
      </PageColumn>
    );
  }

  const display = getTaskDisplay(task.type);
  const handle = instagramUsername
    ? instagramUsername.startsWith('@')
      ? instagramUsername
      : `@${instagramUsername}`
    : null;
  const keywords = task.requiredKeywords ?? [];
  // Creator can only submit a performable/rejected task, and only with a connected account.
  const canSubmit = isConnected && isSupportedTaskType(task.type) && isTaskSubmittable(task.state);
  const validating = isTaskValidating(task.state);
  const rejected = isTaskRejected(task.state);
  const rejectionReason = rejected
    ? parseTaskRejectionReason(task.stateReason, task.type).message
    : '';

  return (
    <PageColumn className="py-4 flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <IconButton
          icon="chevron-left"
          onClick={() => router.history.back()}
          aria-label="Go back"
        />
        <Typography variant="header" size="h1" as="h1">
          {campaign.name ?? 'Your campaign'}
        </Typography>
      </div>

      {validating ? (
        <VerifyingState />
      ) : (
        <>
          <Typography variant="header" size="h2" as="h2">
            Follow these steps below on {display.platform}
          </Typography>

          <ol className="flex flex-col gap-5">
            <NumberedStep index={1} title="Log in to the account below to post">
              {handle ? (
                <ValuePill value={handle} />
              ) : (
                <Typography variant="body" size="small" color="bodylight">
                  Make sure you post from the Instagram account you connected to Influenster.
                </Typography>
              )}
            </NumberedStep>

            <NumberedStep index={2} title="Add the content you've captured" />

            {keywords.length > 0 && (
              <NumberedStep index={3} title="Mention these handles:">
                <div className="flex flex-col gap-2">
                  {keywords.map((keyword) => (
                    <ValuePill key={keyword} value={keyword} />
                  ))}
                </div>
              </NumberedStep>
            )}
          </ol>

          <div className="flex flex-col gap-1 rounded-large bg-surface p-5 text-center shadow-(--shadow-top-light-first-floor)">
            <Typography variant="header" size="h3" as="h3">
              Final check
            </Typography>
            <Typography variant="body" size="small" color="bodylight">
              Please ensure every step above is done to complete this task.
            </Typography>
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="header" size="h2" as="h2">
              Need help?
            </Typography>
            <Button variant="tertiary" size="small" onClick={handleHelpStub}>
              How to post on Instagram
            </Button>
          </div>

          {rejected ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-center gap-2 text-center">
                <span className="flex-shrink-0">
                  <Icon name="alert" size="small" color="warning" />
                </span>
                <Typography variant="body" size="small" color="error">
                  {rejectionReason}
                </Typography>
              </div>
              <div className="flex justify-center">
                <Button variant="tertiary" size="small" onClick={() => setShowReasons(true)}>
                  Learn more
                </Button>
              </div>
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={handleSubmit}
                disabled={!canSubmit || submitTask.isPending}
                isLoading={submitTask.isPending}
              >
                Verify again
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="text-center">
                <Typography variant="body" size="small" color="bodylight">
                  {isConnected
                    ? 'Have you published your post?'
                    : 'Connect your Instagram account in settings to submit this task.'}
                </Typography>
              </div>
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={handleSubmit}
                disabled={!canSubmit || submitTask.isPending}
                isLoading={submitTask.isPending}
              >
                I've posted!
              </Button>
            </div>
          )}
        </>
      )}

      <ReasonsForFailureModal isOpen={showReasons} onClose={() => setShowReasons(false)} />
    </PageColumn>
  );
}

export default PostOnInstagram;
